import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Minus, Plus, Heart, Truck, ShieldCheck, RotateCcw } from 'lucide-react';
import { useProduct, useProducts } from '../hooks/useProducts';
import { useStore } from '../context/StoreContext';
import ProductCard from '../components/site/ProductCard';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const R = (n) => `R ${Number(n).toFixed(0)}`;

export default function ProductPage() {
  const { id } = useParams();
  const { product: p, loading } = useProduct(id);
  const { products } = useProducts();
  const [qty, setQty] = useState(1);
  const { addToCart, toggleWish, wishlist, cacheProduct } = useStore();

  React.useEffect(() => { if (p && cacheProduct) cacheProduct(p); }, [p, cacheProduct]);

  if (loading && !p) return <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8"><div className="aspect-[4/5] max-w-xl animate-pulse bg-champagne" /></div>;
  if (!p) return <Navigate to="/shop" replace />;
  const related = products.filter((x) => x.category === p.category && x.id !== p.id).slice(0, 4);

  return (
    <div className="fade-in">
      <nav className="mx-auto max-w-7xl px-5 pt-6 text-xs text-espresso/60 lg:px-8">
        <Link to="/" className="hover:text-gold">Home</Link> / <Link to="/shop" className="hover:text-gold">Shop</Link> / <span className="text-espresso">{p.title}</span>
      </nav>
      <section className="mx-auto grid max-w-7xl gap-10 px-5 py-10 lg:grid-cols-2 lg:px-8">
        <div className="bg-champagne"><img src={p.image} alt={p.title} className="aspect-[4/5] w-full object-cover" /></div>
        <div>
          <p className="eyebrow text-espresso/60">{p.category}</p>
          <h1 className="mt-2 font-display text-4xl md:text-5xl">{p.title}</h1>
          {p.tagline && <p className="mt-3 text-espresso/70">{p.tagline}</p>}
          <p className="mt-6 text-2xl">{R(p.price)}</p>
          {p.description && <p className="mt-6 max-w-prose whitespace-pre-line text-espresso/80">{p.description}</p>}

          <div className="mt-8 flex items-center gap-4">
            <div className="flex items-center border border-stone-warm">
              <button className="p-3" onClick={() => setQty((q) => Math.max(1, q - 1))}><Minus className="size-3" /></button>
              <span className="w-10 text-center text-sm">{qty}</span>
              <button className="p-3" onClick={() => setQty((q) => q + 1)}><Plus className="size-3" /></button>
            </div>
            <button onClick={() => addToCart(p.id, qty, p)} className="btn btn-dark flex-1">Add to bag</button>
            <button onClick={() => toggleWish(p.id)} aria-label="Wishlist" className="grid size-12 place-items-center border border-stone-warm transition hover:border-gold">
              <Heart className={`size-4 ${wishlist.includes(p.id) ? 'fill-espresso' : ''}`} />
            </button>
          </div>
          <button onClick={() => addToCart(p.id, qty, p)} className="mt-3 btn btn-primary w-full">Buy now</button>

          <div className="mt-8 grid grid-cols-3 gap-3 text-center text-xs text-espresso/70">
            <div className="border border-stone-warm p-3"><Truck className="mx-auto mb-1 size-4" />Free over R 800</div>
            <div className="border border-stone-warm p-3"><RotateCcw className="mx-auto mb-1 size-4" />14-day returns</div>
            <div className="border border-stone-warm p-3"><ShieldCheck className="mx-auto mb-1 size-4" />Secure checkout</div>
          </div>

          <Accordion type="single" collapsible className="mt-8">
            {p.ingredients && <AccordionItem value="details"><AccordionTrigger>Details</AccordionTrigger><AccordionContent><p>{p.ingredients}</p>{p.size && <p className="mt-2 text-espresso/60">Size: {p.size}</p>}</AccordionContent></AccordionItem>}
            <AccordionItem value="ship"><AccordionTrigger>Shipping & returns</AccordionTrigger><AccordionContent>Delivered within 2–5 business days across South Africa. Free shipping on orders over R 800. 14-day returns on unopened products.</AccordionContent></AccordionItem>
            <AccordionItem value="care"><AccordionTrigger>How to use / care</AccordionTrigger><AccordionContent>Follow the recommended usage. For jewelry, avoid harsh chemicals and store in a dry place.</AccordionContent></AccordionItem>
          </Accordion>
        </div>
      </section>

      {related.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
          <h2 className="font-display text-3xl md:text-4xl">You may also love</h2>
          <div className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((rp) => <ProductCard key={rp.id} p={rp} />)}
          </div>
        </section>
      )}
    </div>
  );
}
